import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import HotNewsComments from "component/news/HotNewsCard.jsx";
import NewsCard from "component/news/NewsCard.jsx";
import TopNews from "component/news/TopNews.jsx";
import useBreakpoint from "utils/useBreakPoint.js";
import AllNews from "component/news/AllNews.jsx";
import { TopNewsSkeleton, NewsCardSkeleton, HotNewsSkeleton, AllNewsSkeleton } from "component/news/NewsSkeletons.jsx";
import { getUserId } from "utils/auth.js";
import {
  NEWS_PLACEHOLDER_URL,
  getHotNews,
  getLatestNews,
  getLatestNewsList,
} from "modules/News/api";

const PAGE_SIZE = 20;

const NewsList = () => {
  const { t } = useTranslation();
  const newsPlaceholder = NEWS_PLACEHOLDER_URL;
  const [viewMode, setViewMode] = useState("list");
  const breakpoint = useBreakpoint();
  const [hotNews, setHotNews] = useState([]);
  const [loadingHot, setLoadingHot] = useState(true);
  const [loadingHotMore, setLoadingHotMore] = useState(false);
  const [hotHasMore, setHotHasMore] = useState(true);
  const [errorHot, setErrorHot] = useState(null);
  const [latestNews, setLatestNews] = useState([]);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [loadingLatestMore, setLoadingLatestMore] = useState(false);
  const [latestHasMore, setLatestHasMore] = useState(true);
  const [errorLatest, setErrorLatest] = useState(null);
  const [bestMatchNews, setBestMatchNews] = useState([]);
  const [loadingBestMatch, setLoadingBestMatch] = useState(true);
  const [loadingBestMatchMore, setLoadingBestMatchMore] = useState(false);
  const [bestMatchHasMore, setBestMatchHasMore] = useState(true);
  const [errorBestMatch, setErrorBestMatch] = useState(null);

  const hotNewsRef = useRef([]);
  const latestNewsRef = useRef([]);
  const bestMatchNewsRef = useRef([]);
  const loaderRef = useRef(null);

  const renderBestMatchSkeleton = (count = 3) => (
    <>
      <div className="col-12">
        <TopNewsSkeleton />
      </div>
      {Array.from({ length: count }).map((_, idx) => (
        <div className="col-12" key={`bestmatch-skeleton-${idx}`}>
          <NewsCardSkeleton viewMode={viewMode} />
        </div>
      ))}
    </>
  );

  const renderHotSkeleton = (count = 3) => (
    <>
      {Array.from({ length: count }).map((_, idx) => (
        <div className="mt-4" key={`hot-skeleton-${idx}`}>
          <HotNewsSkeleton />
        </div>
      ))}
    </>
  );

  const renderLatestSkeleton = (count = 4) => (
    <>
      {Array.from({ length: count }).map((_, idx) => (
        <div className="mt-4" key={`latest-skeleton-${idx}`}>
          <AllNewsSkeleton />
        </div>
      ))}
    </>
  );

  useEffect(() => {
    hotNewsRef.current = hotNews;
  }, [hotNews]);

  useEffect(() => {
    latestNewsRef.current = latestNews;
  }, [latestNews]);

  useEffect(() => {
    bestMatchNewsRef.current = bestMatchNews;
  }, [bestMatchNews]);

  const fetchHot = useCallback(async ({ append = false, signal } = {}) => {
    const offset = append ? hotNewsRef.current.length : 0;
    const setLoadingFn = append ? setLoadingHotMore : setLoadingHot;
    setErrorHot(null);
    setLoadingFn(true);
    try {
      const data = await getHotNews({
        limit: PAGE_SIZE,
        offset,
        signal,
      });
      if (signal?.aborted) return;
      const normalized = Array.isArray(data) ? data : [];
      setHotNews((prev) => (append ? [...prev, ...normalized] : normalized));
      setHotHasMore(normalized.length === PAGE_SIZE);
    } catch (e) {
      if (e?.name === "AbortError" || signal?.aborted) return;
      setErrorHot(e.message || t("common.error.unknown"));
    } finally {
      setLoadingFn(false);
    }
  }, [t]);

  const fetchLatest = useCallback(async ({ append = false, signal } = {}) => {
    const offset = append ? latestNewsRef.current.length : 0;
    const setLoadingFn = append ? setLoadingLatestMore : setLoadingLatest;
    setErrorLatest(null);
    setLoadingFn(true);
    try {
      const uid = getUserId();
      const data = await getLatestNews({
        userId: uid || undefined,
        limit: PAGE_SIZE,
        offset,
        signal,
      });
      if (signal?.aborted) return;
      const normalized = Array.isArray(data) ? data : [];
      setLatestNews((prev) => (append ? [...prev, ...normalized] : normalized));
      setLatestHasMore(normalized.length === PAGE_SIZE);
    } catch (e) {
      if (e?.name === "AbortError" || signal?.aborted) return;
      setErrorLatest(e.message || t("common.error.unknown"));
    } finally {
      setLoadingFn(false);
    }
  }, [t]);

  const fetchBestMatch = useCallback(async ({ append = false, signal } = {}) => {
    const offset = append ? bestMatchNewsRef.current.length : 0;
    const setLoadingFn = append ? setLoadingBestMatchMore : setLoadingBestMatch;
    setErrorBestMatch(null);
    setLoadingFn(true);
    try {
      const uid = getUserId();
      const data = await getLatestNewsList({
        userId: uid || undefined,
        limit: PAGE_SIZE,
        offset,
        minMatchRate: 0.6,
        signal,
      });
      if (signal?.aborted) return;
      const normalized = Array.isArray(data) ? data : [];
      setBestMatchNews((prev) => (append ? [...prev, ...normalized] : normalized));
      setBestMatchHasMore(normalized.length === PAGE_SIZE);
    } catch (e) {
      if (e?.name === "AbortError" || signal?.aborted) return;
      setErrorBestMatch(e.message || t("common.error.unknown"));
    } finally {
      setLoadingFn(false);
    }
  }, [t]);

  useEffect(() => {
    const controller = new AbortController();
    fetchHot({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchHot]);

  useEffect(() => {
    const controller = new AbortController();
    fetchLatest({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchLatest]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBestMatch({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchBestMatch]);

  const loadMore = useCallback(() => {
    if (hotHasMore && !loadingHot && !loadingHotMore) {
      fetchHot({ append: true });
    }
    if (latestHasMore && !loadingLatest && !loadingLatestMore) {
      fetchLatest({ append: true });
    }
    if (bestMatchHasMore && !loadingBestMatch && !loadingBestMatchMore) {
      fetchBestMatch({ append: true });
    }
  }, [
    bestMatchHasMore,
    fetchBestMatch,
    fetchHot,
    fetchLatest,
    hotHasMore,
    latestHasMore,
    loadingBestMatch,
    loadingBestMatchMore,
    loadingHot,
    loadingHotMore,
    loadingLatest,
    loadingLatestMore,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;
      if (window.innerHeight + window.scrollY >= scrollHeight - 600) {
        loadMore();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMore]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: "400px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div>
      <div className="main__content__box ">
        <div className="container-fluid px-0">
          <div className="row g-4">
            {breakpoint === "laptop" && (
              <div className="col" style={!["tablet", "mobile"].includes(breakpoint) ? { maxWidth: "850px", flex: "0 1 850px" } : undefined}>
              <div className="row g-4" >
                {loadingBestMatch && renderBestMatchSkeleton()}
                {errorBestMatch && !loadingBestMatch && (
                  <div className="col-12 text-danger small">{errorBestMatch}</div>
                )}
                {!loadingBestMatch && !errorBestMatch && Array.isArray(bestMatchNews) && bestMatchNews.length > 0 && (
                  <>
                    {/* First element as TopNews */}
                    <div className="col-12">
                      <TopNews
                        title={bestMatchNews[0].title || bestMatchNews[0].seo_title || t("news.untitled")}
                        summary={bestMatchNews[0].summary || (bestMatchNews[0].llm_output && bestMatchNews[0].llm_output.summary) || ""}
                        _id={bestMatchNews[0].id || bestMatchNews[0].display_article_id}
                        progress={bestMatchNews[0].match_rate || 0}
                        image_url={bestMatchNews[0].image_url || newsPlaceholder}
                        sectors={bestMatchNews[0].sectors || []}
                        created_at={bestMatchNews[0].recent_at}
                        reading_time_minutes={bestMatchNews[0].reading_time_minutes}
                      />
                    </div>
                    {/* Remaining as NewsCard */}
                    {bestMatchNews.slice(1).map((item, idx) => (
                      <div className="col-12" key={(item.id || item.display_article_id || idx) + "-laptop"}>
                        <NewsCard
                          value={idx + 1}
                          viewMode={viewMode}
                          title={item.title || item.seo_title || t("news.untitled")}
                          summary={item.summary || (item.llm_output && item.llm_output.summary) || ""}
                          _id={item.id || item.display_article_id}
                          progress={item.match_rate || 0}
                          image_url={item.image_url || newsPlaceholder}
                          sectors={item.sectors || []}
                          created_at={item.recent_at}
                          reading_time_minutes={item.reading_time_minutes}
                        />
                      </div>
                    ))}
                    {loadingBestMatchMore && (
                      <div className="col-12">
                        <NewsCardSkeleton viewMode={viewMode} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
            <div
              className={`${["tablet", "mobile"].includes(breakpoint) ? "col-md-12" : "col-md-auto"}`}
              style={!["tablet", "mobile"].includes(breakpoint) ? { maxWidth: "480px", flex: "0 1 480px" } : undefined}
            >
              <div className="news_comments news_comments--fixed">
                {/* Tabs */}
                <ul className="news-tabs" role="tablist">
                  {/* ✅ YOUR FEED (only on tablet & mobile, shown first) */}
                  {["tablet", "mobile"].includes(breakpoint) && (
                    <li className="news-tab-item" role="presentation">
                      <button
                        className="news-tab-btn active"
                        id="feed-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#yourFeed"
                        type="button"
                        role="tab"
                        aria-controls="yourFeed"
                        aria-selected="true"
                      >
                        {t("news.tabs.yourFeed")}
                      </button>
                    </li>
                  )}

                  {/* HOT NEWS (second) */}
                  <li className="news-tab-item" role="presentation">
                    <button
                      className={`news-tab-btn ${!["tablet", "mobile"].includes(breakpoint) ? "active" : ""}`}
                      id="hot-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#hotNews"
                      type="button"
                      role="tab"
                      aria-controls="hotNews"
                      aria-selected={!["tablet", "mobile"].includes(breakpoint)}
                    >
                      {t("news.tabs.hotNews")}
                    </button>
                  </li>

                  {/* ALL NEWS (last) */}
                  <li className="news-tab-item" role="presentation">
                    <button
                      className="news-tab-btn"
                      id="all-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#allNews"
                      type="button"
                      role="tab"
                      aria-controls="allNews"
                      aria-selected="false"
                    >
                      {t("news.tabs.allNews")}
                    </button>
                  </li>
                </ul>

                {/* Tab Content */}
                <div className="news-tab-content tab-content mt-4" id="newsTabContent">
                  {/* ✅ YOUR FEED (tablet + mobile only, default active there) */}
                  {["tablet", "mobile"].includes(breakpoint) && (
                    <div
                      className="tab-pane fade show active"
                      id="yourFeed"
                      role="tabpanel"
                      aria-labelledby="feed-tab"
                    >
                      <div className="row g-4">
                      {loadingBestMatch && renderBestMatchSkeleton()}
                      {errorBestMatch && !loadingBestMatch && (
                        <div className="col-12 text-danger small">{errorBestMatch}</div>
                      )}
                      {!loadingBestMatch && !errorBestMatch && Array.isArray(bestMatchNews) && bestMatchNews.length > 0 && (
                        <>
                          <div className="col-12">
                            <TopNews
                              title={bestMatchNews[0].title || bestMatchNews[0].seo_title || t("news.untitled")}
                              summary={bestMatchNews[0].summary || (bestMatchNews[0].llm_output && bestMatchNews[0].llm_output.summary) || ""}
                              _id={bestMatchNews[0].id || bestMatchNews[0].display_article_id}
                              progress={bestMatchNews[0].match_rate || 0}
                              image_url={bestMatchNews[0].image_url || newsPlaceholder}
                              sectors={bestMatchNews[0].sectors || []}
                              created_at={bestMatchNews[0].recent_at}
                              reading_time_minutes={bestMatchNews[0].reading_time_minutes}
                            />
                          </div>
                          {bestMatchNews.slice(1).map((item, idx) => (
                            <div className="col-12" key={(item.id || item.display_article_id || idx) + "-feed"}>
                              <NewsCard
                                value={idx + 1}
                                viewMode={viewMode}
                                title={item.title || item.seo_title || t("news.untitled")}
                                summary={item.summary || (item.llm_output && item.llm_output.summary) || ""}
                                _id={item.id || item.display_article_id}
                                progress={item.match_rate || 0}
                                image_url={item.image_url || newsPlaceholder}
                                sectors={item.sectors || []}
                                created_at={item.recent_at}
                                reading_time_minutes={item.reading_time_minutes}
                              />
                            </div>
                          ))}
                          {loadingBestMatchMore && (
                            <div className="col-12">
                              <NewsCardSkeleton viewMode={viewMode} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    </div>
                  )}

                  {/* HOT NEWS */}
                  <div
                    className={`tab-pane fade ${!["tablet", "mobile"].includes(breakpoint) ? "show active" : ""}`}
                    id="hotNews"
                    role="tabpanel"
                    aria-labelledby="hot-tab"
                  >
                    {loadingHot && renderHotSkeleton()}
                    {errorHot && (
                      <div className="mt-4 text-danger small">{errorHot}</div>
                    )}
                    {!loadingHot && !errorHot && hotNews.map((item) => (
                      <div className="mt-4" key={item.id}>
                        <HotNewsComments title={item.title} summary={item.summary} linkCount={item.link_count} _id={item.id} source_logos={item.source_logos} growth_pct={item.growth_pct} />
                      </div>
                    ))}
                    {loadingHotMore && renderHotSkeleton(1)}
                  </div>

                  {/* ALL NEWS */}
                  <div
                    className="tab-pane fade"
                    id="allNews"
                    role="tabpanel"
                    aria-labelledby="all-tab"
                  >
                    {loadingLatest && renderLatestSkeleton()}
                    {errorLatest && (
                      <div className="mt-4 text-danger small">{errorLatest}</div>
                    )}
                    {!loadingLatest && !errorLatest && Array.isArray(latestNews) && latestNews.length === 0 && (
                      <div className="mt-4 text-light">{t("news.noNewsYet")}</div>
                    )}
                    {!loadingLatest && !errorLatest && Array.isArray(latestNews) && latestNews.map((item) => (
                      <div className="mt-4" key={item.id || item.display_article_id}>
                        <AllNews
                          title={item.title || item.seo_title}
                          summary={item.summary || (item.llm_output && item.llm_output.summary) || ""}
                          _id={item.id}
                          created_at={item.recent_at}
                          sectors={item.sectors || []}
                          progress={item.match_rate || 0}
                          reading_time_minutes={item.reading_time_minutes}
                        />
                      </div>
                    ))}
                    {loadingLatestMore && renderLatestSkeleton(1)}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      <div ref={loaderRef} />
    </div>
  );
};

export default NewsList;
